import { FC, HTMLAttributes, useRef } from 'react';
import { createPortal } from 'react-dom';
import clx from 'classnames';
import StatusIcon from '../StatusIcon/StatusIcon';
import { useOutsideClick, useToggle } from 'src/Hooks';
import { getGlobalOffset } from 'src/Utils';
import { FileTaskStatusType, FileTaskStatuses } from 'src/Types/Task';
import st from './TaskStatusSelect.module.scss';

interface ITaskStatusSelectProps extends HTMLAttributes<HTMLDivElement> {
	defaultOpen?: boolean;
	disabled?: boolean;

	customStatus?: string;

	value: string;
	setValue?: (value?: string) => void;

	onOutsideClick?: (e: any) => void;
}

export const TaskStatusSelect: FC<ITaskStatusSelectProps> = (props) => {
	const {
		value,
		setValue,
		defaultOpen = false,
		customStatus,
		disabled = false,
		className,
		onOutsideClick,
		...otherProps
	} = props;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const modalRef = useOutsideClick<HTMLDivElement>(
		(e) => {
			if (onOutsideClick) {
				onOutsideClick(e);
			} else if (isOpened) {
				toggle();
			}
		},
		undefined,
		wrapperRef,
	);
	const [isOpened, toggle] = useToggle(defaultOpen);

	const onSelected = (participant: FileTaskStatusType) => {
		if (disabled || !setValue) {
			return;
		}
		setValue(participant.code);
		toggle();
	};

	const getStylesForList = () => {
		const styles = {
			left: 0,
			top: 0,
		};
		if (wrapperRef.current) {
			const offset = getGlobalOffset(wrapperRef.current);
			styles.left = offset.left - offset.scroll;
			styles.top = offset.top + offset.height - offset.scrollTop;
		}
		return styles;
	};

	const renderOption = (item: FileTaskStatusType, index: number) => {
		return (
			<li
				key={'item_' + index}
				className={clx(st.item, { [st.active]: item.code === value })}
				onClick={() => {
					return onSelected(item);
				}}
			>
				<StatusIcon iconName={item.icon} min />
				<span>{item.text}</span>
			</li>
		);
	};

	const renderBody = () => {
		return (
			<div
				ref={modalRef}
				className={st.customerListWrapper}
				style={getStylesForList()}
			>
				<ul className={st.wrapper}>
					{FileTaskStatuses.map(renderOption)}
				</ul>
			</div>
		);
	};

	const classes = clx(st.customerSelect, className, {
		[st.opened]: isOpened,
		[st.disabled]: disabled,
	});

	return (
		<div ref={wrapperRef} {...otherProps} className={classes}>
			<div
				className={st.control}
				onClick={() => {
					return !disabled && toggle();
				}}
			>
				<StatusIcon iconName={FileTaskStatuses[+value].icon} min />
				<span>
					{customStatus
						? customStatus
						: FileTaskStatuses[+value].text}
				</span>
				{!disabled && (
					<svg
						width="33"
						height="32"
						viewBox="0 0 33 32"
						fill="inherit"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M11.5 13.5L16.5 18.5L21.5 13.5H11.5Z"
							fill="inherit"
						/>
					</svg>
				)}
			</div>

			{isOpened && createPortal(renderBody(), document.body)}
		</div>
	);
};

export default TaskStatusSelect;
